<template>
  <div>
    <!-- Assign Roles Modal -->
    <AppModal v-model="showAssignRolesForm" title="Assign Role to User" size="lg" hide-footer>
      <div class="space-y-4">
        <!-- Search staff -->
        <div class="flex gap-2 border border-gray-200 rounded-md overflow-hidden">
          <input
            type="text"
            class="flex-1 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-0 text-gray-700"
            v-model="searchModal"
            @keyup="onSearchModal"
            placeholder="Search staff by name..."
          />
          <button class="px-3 text-gray-400 hover:text-primary bg-gray-50 border-l border-gray-200"
                  @click="onSearchModal">
            <i class="fas fa-search"></i>
          </button>
        </div>

        <!-- Staff list -->
        <div class="border border-gray-100 rounded-md max-h-52 overflow-y-auto">
          <div v-if="loadingAssignment" class="py-6 text-center">
            <i class="fa fa-spinner fa-spin text-primary text-lg"></i>
          </div>
          <div
            v-else
            v-for="(registrar, idx) in getRegistrars"
            :key="idx"
            class="flex items-center justify-between px-3 py-2 border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
            :class="selectedId === registrar.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''"
            @click="selectUser(registrar)"
          >
            <div>
              <p class="text-sm font-medium text-gray-800">
                {{ registrar.givenName }} {{ registrar.surName }}
                <span v-if="registrar.middleName"> {{ registrar.middleName }}</span>
              </p>
              <p class="text-xs text-gray-400">{{ registrar.email }}</p>
            </div>
            <i v-if="selectedId === registrar.id" class="fa fa-check-circle text-primary"></i>
          </div>
          <div v-if="!loadingAssignment && getRegistrars.length === 0" class="py-4 text-center text-xs text-gray-400">
            No staff found. Search to load results.
          </div>
        </div>

        <!-- Role selector -->
        <select v-model="assignForm.roleId"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
          <option v-for="(option, idx) in rolesDataList" :key="option.value ?? `role-${idx}`" :value="option.value">
            {{ option.text }}
          </option>
        </select>

        <div class="flex justify-end">
          <button
            class="btn-primary"
            :disabled="!assignForm.userId || !assignForm.roleId || spinner"
            :class="(!assignForm.userId || !assignForm.roleId || spinner) ? 'opacity-50 cursor-not-allowed' : ''"
            @click="assignNewRole"
          >
            <i class="fa fa-spinner fa-spin mr-1" v-if="spinner"></i>
            <i class="fa fa-user-plus mr-1" v-else></i>
            Assign Role
          </button>
        </div>

        <p v-if="errorMessage" class="text-xs text-red-500">{{ errorMessage }}</p>
      </div>
    </AppModal>

    <!-- Filter bar -->
    <div class="flex items-center justify-between py-3 gap-3">
      <div class="grid grid-cols-3 gap-3 flex-1">
        <input
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          v-model="search"
          placeholder="Search by name"
          @keyup.enter="fetch"
        />
        <select v-model="type"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white">
          <option :value="null">-- Filter by Gender --</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
        </select>
        <select v-model="status"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white">
          <option :value="null">-- Filter by Status --</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>
      <div class="flex gap-2 shrink-0">
        <button class="btn-primary" @click="fetch">
          <i class="fa fa-search mr-1"></i> Search
        </button>
        <button class="btn-primary" @click="openAssignRolesForm">
          <i class="fa fa-user-plus mr-1"></i> Assign Roles
        </button>
            </div>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>Name</th>
          <th>ID Number</th>
          <th>Roles</th>
          <th>Status</th>
        </tr>
        </thead>
        <tbody>
        <!-- Skeleton -->
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td>
              <div class="h-3 bg-gray-200 rounded w-36"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-28"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-24"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-14"></div>
            </td>
          </tr>
        </template>

        <template v-else>
          <tr v-for="(account, idx) in accounts" :key="idx">
            <td class="font-semibold uppercase">
              {{ account.givenName }} {{ account.surName }}
              <span v-if="account.middleName && account.middleName !== 'null'" class="mx-1">{{
                  account.middleName
                }}</span>
            </td>
            <td>
              <span class="uppercase">{{ account.idNumber.split(",")[0] }}</span>
              <br />
              <small class="text-green-600">{{ cleanUnderscoreString(account.idType) }}</small>
            </td>
            <td>
              <div class="flex flex-wrap gap-1">
                                <span
                                  v-for="(role, ridx) in account.userRoles"
                                  :key="ridx"
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                                >
                                    {{ cleanUnderscoreString(role.name.replace('ROLE', '').replace('USER', '')) }}
                                </span>
                <span v-if="!account.userRoles?.length" class="text-xs text-gray-400 italic">No roles</span>
              </div>
            </td>
            <td>
              <span v-if="account.enabled"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
              <span v-else
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Inactive</span>
            </td>
          </tr>
        </template>

        <tr v-if="!loading && accounts.length === 0">
          <td colspan="4" class="text-center py-6 text-gray-400 text-sm">No accounts found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-4 px-1">
      <button
        class="btn-sm-primary"
        :disabled="currentPage <= 1"
        :class="currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
        @click="previous"
      >
        <i class="fa fa-chevron-left mr-1"></i> Previous
      </button>
      <span class="text-xs text-gray-500">Page {{ currentPage }} of {{ pagination.totalPages }}</span>
      <button
        class="btn-sm-primary"
        :disabled="currentPage >= pagination.totalPages"
        :class="currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
        @click="next"
      >
        Next <i class="fa fa-chevron-right ml-1"></i>
      </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { api } from "@/app/services/api.service.ts";
import { cleanUnderscoreString } from "@/commons/composables/transformations.ts";
import { objectToQueryParams } from "@/commons/composables/utilities.ts";
import { useAccountStore } from "@/domains/home/services/accounts/stores";
import { useRolesStore } from "@/stores/roles";

interface Account {
    id: string
    givenName: string
    surName: string
    middleName: string
    idNumber: string
    idType: string
    email: string
    enabled: boolean
    userRoles: UserRole[]
}

interface UserRole {
    name: string
}

interface Registrar {
    id: string
    givenName: string
    surName: string
    middleName?: string
    email: string
}

interface AssignForm {
    userId: string | null
    roleId: string | null
}

interface SelectOption {
    text: string
    value: string | null
    disabled?: boolean
}

interface Pagination {
    totalElements: number
    totalPages: number
}

const emit = defineEmits<{ finished: [value: boolean] }>();

const accountStore = useAccountStore();
const rolesStore = useRolesStore();

const showAssignRolesForm = ref(false)
const loading = ref(false)
const spinner = ref(false)
const currentPage = ref(1);
const limit = ref(200)
const accounts = ref<Account[]>([])
const assignForm = ref<AssignForm>({ userId: null, roleId: null });
const pagination = ref<Pagination>({ totalElements: 0, totalPages: 0 });
const search = ref("")
const searchModal = ref("")
const status = ref<string | null>(null)
const type = ref<string | null>(null)
const rolesDataList = ref<SelectOption[]>([{ text: "Select Role", value: null, disabled: true }])
const getRegistrars = ref<Registrar[]>([])
const loadingAssignment = ref(false)
const selectedId = ref<string | null>(null)
const errorMessage = ref<string | null>(null)

onMounted(() => {
    fetch()
  rolesStore.fetchRoles();
})

const fetch = () => {
    loading.value = true
  const filters: any = { accountType: "BACKOFFICE" };
  if (status.value) filters.accountStatus = status.value;
    filters.perPage = limit.value
  accountStore.fetchStaffAccounts(objectToQueryParams(filters));
}

const onSearchModal = () => {
    searchRegistrars({ term: searchModal.value })
}

const searchRegistrars = async (data: { term: string }) => {
    loadingAssignment.value = true
    errorMessage.value = null
    getRegistrars.value = []
    await api()
        .get("auth/api/users/staff?name=" + data.term.toUpperCase())
        .then(response => {
          getRegistrars.value = response?.data?.data?.content ?? [];
            loadingAssignment.value = false
        })
        .catch(error => {
            loadingAssignment.value = false
          errorMessage.value = error.response?.data?.message;
        })
}

const selectUser = (user: Registrar) => {
    assignForm.value.userId = user.id
    selectedId.value = user.id
}

const assignNewRole = () => {
    spinner.value = true
    errorMessage.value = null
  rolesStore.assignRole({
        userId: assignForm.value.userId,
        roleIds: [assignForm.value.roleId]
  })
}

const openAssignRolesForm = () => {
    showAssignRolesForm.value = true
}

const previous = () => {
    if (currentPage.value > 1) {
        currentPage.value--
        fetch()
    }
}

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};

watch(() => accountStore.officer_accounts, (data: any) => {
  loading.value = false;
  if (data?.data) {
    accounts.value = data.data.content ?? [];
    pagination.value.totalElements = data.data.totalElements ?? 0;
    pagination.value.totalPages = data.data.totalPages ?? 0;
    }
})

watch(() => rolesStore.successRole, (data) => {
    if (data) {
        spinner.value = false
      assignForm.value = { userId: null, roleId: null };
      selectedId.value = null;
        showAssignRolesForm.value = false
      fetch();
        emit("finished", true)
    }
})

watch(() => rolesStore.rolesList, (data) => {
  if (!data) return;
  const list = Array.isArray(data) ? data : (data.data ?? []);
  rolesDataList.value = [
    { text: "Select Role", value: null, disabled: true },
    ...list.map((r: any) => ({ text: r.name, value: r.roleId }))
  ];
})
</script>
